// A5 — Maharashtra (photo): two identical receipts side-by-side on one landscape page; Marathi left, Article + Borrower photos side-by-side on the right.
import React from 'react';
import { imageURL } from '../../../imageURL';

export const PLEDGE_A5_TEMPLATE_MAHARASHTRA = 'A5_MAHARASHTRA_PHOTO_STANDARD';

const MAHARASHTRA_FORM_REF_DEFAULT = 'FORM NO-8 & LAW NO.16';

const DEFAULT_MAHARASHTRA_FOOTER =
  'Powered by Riddhi Infotech www.riddhi-infotech.com 7499391121';

const MARATHI_TERMS = [
  'अटी:',
  '(१) गहाण ठेवलेले दागिन्यांची शेवटची मुदत १२ महिने ७ दिवस राहील',
  '(२) १२ महिने झाल्यानंतर दागिन्यांचे व्याज भरून पावती बदली करून घ्यावी',
  '(३) ठेवलेले दागिने वेळेवर न सोडवल्यास तर बिना नोटीस दिले दागिने विकले जातील, जे नुकसान होईल ते तुमच्या कडून वसूल केले जाईल',
  '(४) गहाण ठेवलेल्या वस्तूवर दुसऱ्यांदा कर्ज उपलब्ध होणार नाही',
  '(५) जर पावती हरवली तर वस्तू आमच्या व्यवस्थेनुसार परत केल्या जातील ही माझी वस्तू आहे याची मी खात्री देतो त्याशी संबंधित कागदपत्रांची जबाबदारी माझी आहे',
  '(६) जर वरील दिलेल्या अटींच्या दक्षता नाही घेतली तर आम्ही कोणत्या ही सूचना न देता गहाण ठेवलेली वस्तूची विक्री करू "याची नोंद घ्यावी"',
];

const addMonthsClamped = (date, months) => {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + Number(months || 0));
  if (d.getDate() < day) d.setDate(0);
  return d;
};

const formatAadharDisplay = (raw) => {
  if (raw == null || raw === '') return '________________';
  const d = String(raw).replace(/\D/g, '');
  if (d.length === 12) return `${d.slice(0, 4)} ${d.slice(4, 8)} ${d.slice(8, 12)}`;
  return String(raw);
};

const resolveUploadSrc = (name) => {
  if (!name) return null;
  if (typeof name !== 'string') return null;
  if (name.startsWith('data:') || name.startsWith('http://') || name.startsWith('https://')) return name;
  let file = name.replace(/^\//, '').trim();
  if (/[\\/]/.test(file)) {
    file = file.split(/[/\\]/).pop() || file;
  }
  const base = (imageURL || '').replace(/\/$/, '');
  return `${base}/${file}`;
};

const disbursementTypeLabel = (row) => {
  const t = String(row?.type || '').toLowerCase().replace(/\s+/g, '_');
  if (t === 'cash') return 'Cash';
  if (t === 'bank_transfer')
    return row?.bankLedgerName ? `Bank — ${row.bankLedgerName}` : 'Bank transfer';
  if (t === 'upi') return 'UPI';
  if (t === 'credit_ledger') return 'Credit (no cash)';
  return row?.type ? String(row.type) : '—';
};

const getDisbursementRowsForPrint = (pledgeData, principalNum) => {
  const principal = Number(principalNum) || 0;
  let raw = pledgeData?.disbursementPaymentModes;
  if (!Array.isArray(raw) || raw.length === 0) raw = pledgeData?.paymentModes;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .map((r) => ({
        type: r.type,
        bankLedgerName: r.bankLedgerName,
        amount: Number(r.amount) || 0,
      }))
      .filter((r) => r.amount > 0);
  }
  if (principal <= 0) return [];
  const noCash = String(pledgeData?.accountingDisbursement || '').toUpperCase() === 'NONE';
  return [
    {
      type: noCash ? 'credit_ledger' : 'cash',
      bankLedgerName: null,
      amount: principal,
    },
  ];
};

const PledgePrintA5MaharashtraPhotoStandard = ({ pledgeData, companyData }) => {
  if (!pledgeData) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB');
  };

  const formatAmount = (amount) => {
    if (amount == null || amount === '') return '0.00';
    return parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const c = pledgeData.customer || {};
  const borrowerUpper = `${(c.firstName || '').trim()} ${(c.lastName || '').trim()}`.trim().toUpperCase() || '________________';
  const aadhar = formatAadharDisplay(c.aadharCardNo ?? c.aadhar ?? c.aadharNo);
  const panVal = (c.panCardNo ?? c.pan ?? '').toString().trim() || '________________';
  const nominee = (c.nomineeName ?? c.nominee ?? '').toString().trim() || '________________';
  const addr = (c.address || '').trim() || '________________';
  const mob = (c.mobileNo || '').trim() || '________________';

  const ticketNo = pledgeData.loanDetails?.ticketNo || 'N/A';
  const loanDate = formatDate(pledgeData.date);
  const principal = pledgeData.loanDetails?.principalAmount;
  const principalNum = parseFloat(principal) || 0;
  const disbursementRows = getDisbursementRowsForPrint(pledgeData, principalNum);
  const disbursementTotal = disbursementRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const durationM = pledgeData.loanDetails?.duration ?? 0;
  const maturity = pledgeData.date
    ? formatDate(addMonthsClamped(pledgeData.date, durationM))
    : 'N/A';

  const articles = pledgeData.articles || [];
  const totalApprox = articles.reduce((sum, a) => sum + (parseFloat(a.valueTotal) || 0), 0);

  const articleImg = resolveUploadSrc(pledgeData.articlePhoto);
  const clientImg = resolveUploadSrc(pledgeData.clientPhoto);

  const formRef = companyData?.maharashtraFormRef || MAHARASHTRA_FORM_REF_DEFAULT;
  const companyLine = companyData?.companyName || 'COMPANY NAME';
  const addressLine = companyData?.address || '';
  const tel =
    companyData?.alternateMobileNumber && companyData?.mobileNumber
      ? `${companyData.mobileNumber} / ${companyData.alternateMobileNumber}`
      : companyData?.mobileNumber || 'N/A';

  const thumbImageSrc = pledgeData.fingerprint?.image
    ? pledgeData.fingerprint.image.startsWith('data:')
      ? pledgeData.fingerprint.image
      : `data:image/png;base64,${pledgeData.fingerprint.image}`
    : null;

  const PhotoCell = ({ src, label }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
      <div
        style={{
          width: 68,
          height: 68,
          border: '1px solid #222',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: '#f5f5f5',
          boxSizing: 'border-box',
        }}
      >
        {src ? (
          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 10, color: '#999', padding: 2 }}>—</span>
        )}
      </div>
      <div style={{ fontSize: 10, marginTop: 2, fontWeight: 'bold' }}>{label}</div>
    </div>
  );

  const ReceiptHalf = () => (
    <div
      style={{
        boxSizing: 'border-box',
        padding: '5px 8px 6px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        alignSelf: 'stretch',
        minHeight: 0,
        width: '100%',
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: 10.5,
        lineHeight: 1.22,
        color: '#000',
        overflow: 'visible',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: 4 }}>
        <div style={{ fontWeight: 'bold', fontSize: 13 }}>(LOAN RECEIPT)</div>
        <div style={{ fontWeight: 'bold', fontSize: 14, marginTop: 2 }}>{companyLine}</div>
        {addressLine ? <div style={{ fontSize: 10.5, marginTop: 1 }}>{addressLine}</div> : null}
        <div style={{ fontSize: 10.5 }}>Tel. No.: {tel}</div>
        <div style={{ fontSize: 10.5, fontWeight: 'bold', marginTop: 2 }}>{formRef}</div>
      </div>

      <div style={{ display: 'flex', gap: 7, marginBottom: 4 }}>
        <div style={{ flex: '1 1 58%', minWidth: 0 }}>
          <div>
            <strong>Money Lender License No. :</strong> {companyData?.licenseNumber || 'N/A'}
          </div>
          <div>
            <strong>Form No.</strong> M1-{ticketNo}
          </div>
          <div>
            <strong>BORROWER:</strong> {borrowerUpper}
          </div>
          <div>
            <strong>AADHAR NO</strong> {aadhar}
          </div>
          <div>
            <strong>Address:</strong> {addr}
          </div>
          <div>
            <strong>Mob. :</strong> {mob}
          </div>
        </div>
        <div style={{ flex: '1 1 42%', minWidth: 0 }}>
          <div>
            <strong>Date :</strong> {loanDate}
          </div>
          <div>
            <strong>Nomini :</strong> {nominee}
          </div>
          <div>
            <strong>PAN :</strong> {panVal}
          </div>
        </div>
      </div>

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 9.5,
          marginBottom: 4,
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr>
            {['Description', 'Gr.Wt.', 'Net Wt.', 'Approx Amt'].map((h) => (
              <th
                key={h}
                style={{
                  border: '1px solid #000',
                  padding: '3px 4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  background: '#eee',
                  fontSize: 8.5,
                  lineHeight: 1.15,
                  wordBreak: 'break-word',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {articles.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ border: '1px solid #000', padding: 4, textAlign: 'center' }}>
                —
              </td>
            </tr>
          ) : (
            articles.map((article, index) => (
              <tr key={index}>
                <td style={{ border: '1px solid #000', padding: '3px 4px' }}>
                  {(article.description || article.name || '—').toString().slice(0, 22)}
                </td>
                <td style={{ border: '1px solid #000', padding: '3px 4px', textAlign: 'right' }}>
                  {parseFloat(article.grossWt ?? article.grossWeight ?? 0).toFixed(3)}
                </td>
                <td style={{ border: '1px solid #000', padding: '3px 4px', textAlign: 'right' }}>
                  {parseFloat(article.netWt ?? article.netWeight ?? 0).toFixed(3)}
                </td>
                <td style={{ border: '1px solid #000', padding: '3px 4px', textAlign: 'right' }}>
                  {formatAmount(article.valueTotal).replace(/,/g, '')}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* 2×2 grid: row1 = amount | date; row2 = maturity | approx value */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '3px 5px',
          fontSize: 9.5,
          marginBottom: 4,
          lineHeight: 1.2,
        }}
      >
        <div>
          <strong>Loan Amount :</strong> {formatAmount(principal).replace(/,/g, '')}
        </div>
        <div>
          <strong>Loan Date :</strong> {loanDate}
        </div>
        <div>
          <strong>Maturity Date :</strong> {maturity}
        </div>
        <div>
          <strong>Approx Value :</strong> {formatAmount(totalApprox).replace(/,/g, '')}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 7,
          alignItems: 'flex-start',
          marginBottom: 4,
          flex: '0 0 auto',
        }}
      >
        <div
          style={{
            flex: '1 1 58%',
            minWidth: 0,
            fontSize: 9,
            lineHeight: 1.12,
            textAlign: 'justify',
            padding: '4px 6px',
            border: '1px solid #bbb',
            background: '#fafafa',
            boxSizing: 'border-box',
          }}
        >
          {MARATHI_TERMS.map((line, i) => (
            <div key={i} style={{ marginBottom: i < MARATHI_TERMS.length - 1 ? 2 : 0 }}>
              {line}
            </div>
          ))}
        </div>
        <div
          style={{
            flex: '1 1 42%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 4,
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 7,
              }}
            >
              <PhotoCell src={articleImg} label="Article" />
              <PhotoCell src={clientImg} label="Borrower" />
            </div>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 7.5,
                tableLayout: 'fixed',
              }}
            >
            <thead>
              <tr>
                <th
                  colSpan={2}
                  style={{
                    border: '1px solid #000',
                    padding: '2px 3px',
                    fontWeight: 'bold',
                    textAlign: 'left',
                    background: '#eee',
                    fontSize: 7,
                    lineHeight: 1.15,
                  }}
                >
                  Payment
                </th>
              </tr>
              <tr>
                <th
                  style={{
                    border: '1px solid #000',
                    padding: '2px 3px',
                    fontWeight: 'bold',
                    textAlign: 'left',
                    background: '#f5f5f5',
                    fontSize: 7,
                    width: '58%',
                    lineHeight: 1.15,
                  }}
                >
                  Type
                </th>
                <th
                  style={{
                    border: '1px solid #000',
                    padding: '2px 3px',
                    fontWeight: 'bold',
                    textAlign: 'right',
                    background: '#f5f5f5',
                    fontSize: 7,
                    lineHeight: 1.15,
                  }}
                >
                  Amt
                </th>
              </tr>
            </thead>
            <tbody>
              {disbursementRows.length === 0 ? (
                <tr>
                  <td colSpan={2} style={{ border: '1px solid #000', padding: 3, textAlign: 'center', fontSize: 7 }}>
                    —
                  </td>
                </tr>
              ) : (
                <>
                  {disbursementRows.map((row, idx) => (
                    <tr key={idx}>
                      <td
                        style={{
                          border: '1px solid #000',
                          padding: '2px 3px',
                          wordBreak: 'break-word',
                          fontSize: 7.5,
                          lineHeight: 1.15,
                        }}
                      >
                        {disbursementTypeLabel(row)}
                      </td>
                      <td
                        style={{
                          border: '1px solid #000',
                          padding: '2px 3px',
                          textAlign: 'right',
                          fontSize: 7.5,
                          lineHeight: 1.15,
                        }}
                      >
                        {formatAmount(row.amount).replace(/,/g, '')}
                      </td>
                    </tr>
                  ))}
                  {disbursementRows.length > 1 ? (
                    <tr>
                      <td
                        style={{
                          border: '1px solid #000',
                          padding: '2px 3px',
                          fontWeight: 'bold',
                          textAlign: 'right',
                          fontSize: 7.5,
                        }}
                      >
                        Total
                      </td>
                      <td
                        style={{
                          border: '1px solid #000',
                          padding: '2px 3px',
                          textAlign: 'right',
                          fontWeight: 'bold',
                          fontSize: 7.5,
                        }}
                      >
                        {formatAmount(disbursementTotal).replace(/,/g, '')}
                      </td>
                    </tr>
                  ) : null}
                </>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Grows to push signatures + footer toward bottom of half-page */}
      <div style={{ flex: '1 1 auto', minHeight: 6 }} aria-hidden="true" />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 0,
          gap: 7,
          flex: '0 0 auto',
        }}
      >
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ minHeight: 14 }} />
          <div style={{ borderTop: '1px solid #000', paddingTop: 2, fontSize: 10.5, fontWeight: 'bold' }}>
            Sign Of Donator
          </div>
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ minHeight: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {thumbImageSrc ? (
              <img src={thumbImageSrc} alt="" style={{ maxHeight: 32, maxWidth: 60, objectFit: 'contain' }} />
            ) : null}
          </div>
          <div style={{ borderTop: '1px solid #000', paddingTop: 2, fontSize: 10.5, fontWeight: 'bold' }}>
            Sign Of Borrower
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 4,
          paddingTop: 3,
          borderTop: '1px solid #ccc',
          fontSize: 7.5,
          textAlign: 'center',
          color: '#555',
          lineHeight: 1.2,
          flex: '0 0 auto',
        }}
      >
        {companyData?.printFooter?.trim() ? companyData.printFooter : DEFAULT_MAHARASHTRA_FOOTER}
      </div>
    </div>
  );

  return (
    <div
      className="pledge-print-a5-maharashtra-photo-standard"
      style={{
        width: '100%',
        minHeight: '13.7cm',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        margin: 0,
        padding: 0,
        background: '#fff',
        overflow: 'visible',
      }}
    >
      <div
        style={{
          flex: '1 1 50%',
          minWidth: 0,
          borderRight: '1px dashed #000',
          boxSizing: 'border-box',
          overflow: 'visible',
        }}
      >
        <ReceiptHalf />
      </div>
      <div style={{ flex: '1 1 50%', minWidth: 0, boxSizing: 'border-box', overflow: 'visible' }}>
        <ReceiptHalf />
      </div>
    </div>
  );
};

export default PledgePrintA5MaharashtraPhotoStandard;
